import { useState, useMemo, useRef } from 'react'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { useSettings } from '../../context/SettingsContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCurrencyExact } from '../../lib/formatters'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

// STRS Ohio age factor table (per year of service)
// Based on age at retirement for unreduced benefit
const STRS_OHIO_AGE_FACTORS = {
  55: 2.20, 56: 2.22, 57: 2.24, 58: 2.26, 59: 2.28,
  60: 2.30, 61: 2.32, 62: 2.34, 63: 2.36, 64: 2.38,
  65: 2.20, // base for 5+ years at 65
}

// For 31+ years of service, STRS Ohio uses 2.5% factor
const STRS_OHIO_31_PLUS_FACTOR = 2.50

const PLAN_PRESETS = {
  strs_ohio: {
    label: 'STRS Ohio',
    employeeContrib: 14,
    employerContrib: 14,
    fasYears: 5,
    vestingYears: 5,
    unreducedAge: 65,
    unreducedYears: 35,
    earlyAge: 60,
    earlyYears: 30,
    maxBenefitPercent: 100,
    useAgeFactor: true,
    desc: 'State Teachers Retirement System of Ohio',
  },
  calstrs: {
    label: 'CalSTRS',
    employeeContrib: 10.25,
    employerContrib: 19.1,
    fasYears: 3,
    vestingYears: 5,
    unreducedAge: 65,
    unreducedYears: 30,
    earlyAge: 55,
    earlyYears: 30,
    maxBenefitPercent: 100,
    useAgeFactor: true,
    desc: 'California State Teachers\' Retirement',
  },
  generic: {
    label: 'Custom Plan',
    employeeContrib: 10,
    employerContrib: 10,
    fasYears: 5,
    vestingYears: 5,
    unreducedAge: 65,
    unreducedYears: 30,
    earlyAge: 60,
    earlyYears: 25,
    maxBenefitPercent: 100,
    useAgeFactor: false,
    desc: 'Enter your plan details manually',
  },
}

function getSTRSAgeFactor(retirementAge, yearsOfService) {
  // 35+ years = unreduced at any age with 2.5% factor
  if (yearsOfService >= 35) return 2.50
  // 31+ years of service get the higher factor
  if (yearsOfService >= 31) return STRS_OHIO_31_PLUS_FACTOR
  // Look up age-based factor
  if (retirementAge >= 65) return 2.20
  if (retirementAge <= 55) return 2.20
  return STRS_OHIO_AGE_FACTORS[retirementAge] || 2.20
}

function calculatePension(params) {
  const {
    currentAge,
    retirementAge,
    currentSalary,
    salaryGrowth,
    currentYearsOfService,
    planPreset,
    customMultiplier,
  } = params

  const yearsUntilRetirement = Math.max(0, retirementAge - currentAge)
  const totalYearsOfService = currentYearsOfService + yearsUntilRetirement
  const plan = PLAN_PRESETS[planPreset]

  // Project salary growth year by year
  const salaryHistory = []
  let salary = currentSalary
  for (let i = 0; i <= yearsUntilRetirement; i++) {
    salaryHistory.push(salary)
    salary = Math.round(salary * (1 + salaryGrowth / 100))
  }

  // Final Average Salary = average of highest N years
  const sortedSalaries = [...salaryHistory].sort((a, b) => b - a)
  const fasYears = Math.min(plan.fasYears, salaryHistory.length)
  const fas = sortedSalaries.slice(0, fasYears).reduce((s, v) => s + v, 0) / fasYears

  // Benefit multiplier
  let multiplier
  if (plan.useAgeFactor && planPreset === 'strs_ohio') {
    multiplier = getSTRSAgeFactor(retirementAge, totalYearsOfService)
  } else if (plan.useAgeFactor && planPreset === 'calstrs') {
    // CalSTRS uses age-based factor: 2.0% at 62, 2.4% at 65+
    if (retirementAge >= 65) multiplier = 2.40
    else if (retirementAge >= 63) multiplier = 2.20
    else if (retirementAge >= 62) multiplier = 2.00
    else if (retirementAge >= 60) multiplier = 1.76
    else if (retirementAge >= 55) multiplier = 1.16 + (retirementAge - 55) * 0.12
    else multiplier = 1.16
  } else {
    multiplier = customMultiplier
  }

  // Annual pension = multiplier% × years of service × FAS
  const annualBenefit = (multiplier / 100) * totalYearsOfService * fas
  // Cap at max benefit percent of FAS
  const cappedAnnual = Math.min(annualBenefit, fas * (plan.maxBenefitPercent / 100))
  const monthlyBenefit = cappedAnnual / 12

  // Replacement ratio = pension / final salary
  const finalSalary = salaryHistory[salaryHistory.length - 1]
  const replacementRatio = finalSalary > 0 ? (cappedAnnual / finalSalary) * 100 : 0

  // Eligibility check
  const isVested = totalYearsOfService >= plan.vestingYears
  const unreducedByAge = retirementAge >= plan.unreducedAge && totalYearsOfService >= plan.vestingYears
  const unreducedByService = totalYearsOfService >= plan.unreducedYears
  const earlyEligible = retirementAge >= plan.earlyAge && totalYearsOfService >= plan.earlyYears
  const isUnreduced = unreducedByAge || unreducedByService

  // Contribution totals
  const totalEmployeeContrib = salaryHistory.reduce((sum, s) => sum + s * (plan.employeeContrib / 100), 0)
  const totalEmployerContrib = salaryHistory.reduce((sum, s) => sum + s * (plan.employerContrib / 100), 0)

  // Projection data — benefit at different retirement ages
  const projectionData = []
  for (let age = Math.max(55, currentAge); age <= 70; age++) {
    const projYears = Math.max(0, age - currentAge)
    const projTotalService = currentYearsOfService + projYears
    const projSalaries = []
    let projSal = currentSalary
    for (let i = 0; i <= projYears; i++) {
      projSalaries.push(projSal)
      projSal = Math.round(projSal * (1 + salaryGrowth / 100))
    }
    const projSorted = [...projSalaries].sort((a, b) => b - a)
    const projFasYears = Math.min(plan.fasYears, projSalaries.length)
    const projFas = projSorted.slice(0, projFasYears).reduce((s, v) => s + v, 0) / projFasYears

    let projMult
    if (plan.useAgeFactor && planPreset === 'strs_ohio') {
      projMult = getSTRSAgeFactor(age, projTotalService)
    } else if (plan.useAgeFactor && planPreset === 'calstrs') {
      if (age >= 65) projMult = 2.40
      else if (age >= 63) projMult = 2.20
      else if (age >= 62) projMult = 2.00
      else if (age >= 60) projMult = 1.76
      else if (age >= 55) projMult = 1.16 + (age - 55) * 0.12
      else projMult = 1.16
    } else {
      projMult = customMultiplier
    }

    const projAnnual = Math.min((projMult / 100) * projTotalService * projFas, projFas * (plan.maxBenefitPercent / 100))
    const projFinalSal = projSalaries[projSalaries.length - 1]

    projectionData.push({
      age,
      yearsOfService: projTotalService,
      annualBenefit: Math.round(projAnnual),
      monthlyBenefit: Math.round(projAnnual / 12),
      fas: Math.round(projFas),
      replacementRatio: projFinalSal > 0 ? Math.round((projAnnual / projFinalSal) * 1000) / 10 : 0,
      multiplier: projMult,
    })
  }

  return {
    annualBenefit: Math.round(cappedAnnual),
    monthlyBenefit: Math.round(monthlyBenefit),
    fas: Math.round(fas),
    finalSalary,
    totalYearsOfService,
    multiplier,
    replacementRatio: Math.round(replacementRatio * 10) / 10,
    isVested,
    isUnreduced,
    earlyEligible,
    totalEmployeeContrib: Math.round(totalEmployeeContrib),
    totalEmployerContrib: Math.round(totalEmployerContrib),
    projectionData,
  }
}

export default function RetirementCalculator() {
  const { state } = useFinance()
  const { settings } = useSettings()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)
  const replacementChartRef = useRef(null)
  const d = settings.calculatorDefaults.retirement

  const [planPreset, setPlanPreset] = useState(d.planPreset)
  const [currentAge, setCurrentAge] = useState(d.currentAge)
  const [retirementAge, setRetirementAge] = useState(d.retirementAge)
  const [currentSalary, setCurrentSalary] = useState(d.currentSalary)
  const [salaryGrowth, setSalaryGrowth] = useState(d.salaryGrowth)
  const [currentYearsOfService, setCurrentYearsOfService] = useState(d.yearsOfService)
  const [customMultiplier, setCustomMultiplier] = useState(2.2)

  const plan = PLAN_PRESETS[planPreset]

  const result = useMemo(() =>
    calculatePension({
      currentAge, retirementAge, currentSalary, salaryGrowth,
      currentYearsOfService, planPreset, customMultiplier,
    }),
    [currentAge, retirementAge, currentSalary, salaryGrowth, currentYearsOfService, planPreset, customMultiplier]
  )

  // PDF Export
  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const margin = 14
    let y

    // ── Title ──
    doc.setFontSize(22)
    doc.setTextColor(30, 41, 59)
    doc.text(`${plan.label} Retirement Report`, margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(100, 116, 139)
    doc.text(`Generated by Liquid Finance  •  ${new Date().toLocaleDateString()}`, margin, 30)

    // ── Plan Parameters ──
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Plan Parameters', margin, 42)

    autoTable(doc, {
      head: [['Parameter', 'Value']],
      body: [
        ['Plan', `${plan.label} — ${plan.desc}`],
        ['Employee Contribution', `${plan.employeeContrib}%`],
        ['Employer Contribution', `${plan.employerContrib}%`],
        ['FAS Period', `${plan.fasYears} years`],
        ['Vesting Requirement', `${plan.vestingYears} years`],
        ['Unreduced Benefit Age', `${plan.unreducedAge}`],
        ['Unreduced Benefit Service', `${plan.unreducedYears} years`],
      ],
      startY: 46,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: margin, right: margin },
    })

    // ── Your Details (Inputs) ──
    y = (doc.lastAutoTable?.finalY || 80) + 10
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Your Details', margin, y)

    const inputRows = [
      ['Current Age', `${currentAge} years`],
      ['Retirement Age', `${retirementAge} years`],
      ['Current Annual Salary', formatCurrency(currentSalary)],
      ['Expected Salary Growth', `${salaryGrowth}% / year`],
      ['Current Years of Service', `${currentYearsOfService} years`],
    ]
    if (!plan.useAgeFactor) {
      inputRows.push(['Benefit Multiplier (Custom)', `${customMultiplier}% per year`])
    }

    autoTable(doc, {
      head: [['Input', 'Value']],
      body: inputRows,
      startY: y + 4,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: margin, right: margin },
    })

    // ── Retirement Eligibility ──
    y = (doc.lastAutoTable?.finalY || 80) + 10
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Retirement Eligibility', margin, y)

    const eligibilityRows = [
      ['Vested', result.isVested ? 'Yes' : `No — Need ${plan.vestingYears - result.totalYearsOfService} more years`],
      ['Benefit Status', result.isUnreduced ? 'Unreduced Benefit' : result.earlyEligible ? 'Early Retirement Eligible' : 'Not Yet Eligible'],
    ]
    if (planPreset === 'strs_ohio' && result.totalYearsOfService >= 35) {
      eligibilityRows.push(['Special Rule', '35yr Rule (Any Age) — Qualifies'])
    }
    if (!result.isUnreduced && result.totalYearsOfService < plan.unreducedYears) {
      eligibilityRows.push(['Path to Unreduced', `Need ${plan.unreducedYears - result.totalYearsOfService} more years of service or age ${plan.unreducedAge}`])
    }

    autoTable(doc, {
      head: [['Eligibility Check', 'Status']],
      body: eligibilityRows,
      startY: y + 4,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: margin, right: margin },
    })

    // ── Result Cards (All 8) ──
    doc.addPage()
    doc.setFontSize(16)
    doc.setTextColor(30, 41, 59)
    doc.text('Results Summary', margin, 22)

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: [
        ['Monthly Pension', formatCurrencyExact(result.monthlyBenefit)],
        ['Annual Pension', formatCurrency(result.annualBenefit)],
        ['Replacement Ratio', `${result.replacementRatio}%`],
        ['Benefit Multiplier', `${result.multiplier}%`],
        ['Final Average Salary (FAS)', formatCurrency(result.fas)],
        ['Total Years of Service', `${result.totalYearsOfService}`],
        ['Your Total Contributions', formatCurrency(result.totalEmployeeContrib)],
        ['Employer Total Contributions', formatCurrency(result.totalEmployerContrib)],
      ],
      startY: 28,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { left: margin, right: margin },
      columnStyles: { 1: { fontStyle: 'bold' } },
    })

    // ── Benefit Calculation Breakdown ──
    y = (doc.lastAutoTable?.finalY || 60) + 10
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Benefit Calculation Breakdown', margin, y)
    y += 8
    doc.setFontSize(11)
    doc.setTextColor(51, 65, 85)
    doc.text(
      `${result.multiplier}% (multiplier)  x  ${result.totalYearsOfService} yrs (service)  x  ${formatCurrency(result.fas)} (FAS)  =  ${formatCurrency(result.annualBenefit)}/yr`,
      margin, y
    )
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(100, 116, 139)
    doc.text(`Monthly: ${formatCurrencyExact(result.monthlyBenefit)}   |   Replaces ${result.replacementRatio}% of final salary (${formatCurrency(result.finalSalary)})`, margin, y)

    // ── Age Projection Chart ──
    if (result.projectionData.length > 0) {
      await addChartToPDF(doc, chartRef, { title: 'Benefit by Retirement Age' })
    }

    // ── Replacement Ratio Chart ──
    if (result.projectionData.length > 0) {
      await addChartToPDF(doc, replacementChartRef, { title: 'Income Replacement Ratio' })
      // Add note below chart
      const cY = doc.internal.pageSize.getHeight() - 20
      doc.setFontSize(9)
      doc.setTextColor(100, 116, 139)
      doc.text('Target: 70-80% of final salary to maintain lifestyle in retirement', margin, cY)
    }

    // ── Detailed Projection Table ──
    if (result.projectionData.length > 0) {
      doc.addPage()
      doc.setFontSize(16)
      doc.setTextColor(30, 41, 59)
      doc.text('Detailed Projection Table', margin, 22)

      autoTable(doc, {
        head: [['Age', 'Service', 'FAS', 'Multiplier', 'Annual Pension', 'Monthly Pension', 'Replacement %']],
        body: result.projectionData.map(r => [
          `${r.age}${r.age === retirementAge ? ' (target)' : ''}`,
          `${r.yearsOfService} yr`,
          formatCurrency(r.fas),
          `${r.multiplier}%`,
          formatCurrency(r.annualBenefit),
          formatCurrency(r.monthlyBenefit),
          `${r.replacementRatio}%`,
        ]),
        startY: 28,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8 },
        margin: { left: margin, right: margin },
        didParseCell: (data) => {
          // Highlight the target retirement age row
          if (data.section === 'body') {
            const rowAge = result.projectionData[data.row.index]?.age
            if (rowAge === retirementAge) {
              data.cell.styles.fillColor = [241, 245, 249]
              data.cell.styles.fontStyle = 'bold'
            }
          }
        },
      })
    }

    doc.save(`${planPreset}-retirement-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Plan Presets */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-body mb-3">Retirement Plan</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PLAN_PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  onClick={() => setPlanPreset(key)}
                  className={`flex flex-col px-4 py-2.5 rounded-xl text-sm transition-all ${
                    planPreset === key
                      ? 'text-heading shadow-sm'
                      : 'text-muted hover:text-heading bg-hover'
                  }`}
                  style={planPreset === key ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : {}}
                >
                  <span className="font-medium">{p.label}</span>
                  <span className="text-xs text-faint">{p.desc}</span>
                </button>
              ))}
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
      </GlassCard>

      {/* Inputs */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Your Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField
            label="Current Age"
            value={currentAge}
            onChange={setCurrentAge}
            suffix="years"
            type="number"
            min={20}
            max={70}
          />
          <InputField
            label="Retirement Age"
            value={retirementAge}
            onChange={setRetirementAge}
            suffix="years"
            type="number"
            min={currentAge}
            max={75}
          />
          <InputField
            label="Current Annual Salary"
            value={currentSalary}
            onChange={setCurrentSalary}
            prefix="$"
            type="number"
            min={0}
          />
          <InputField
            label="Expected Salary Growth"
            value={salaryGrowth}
            onChange={setSalaryGrowth}
            suffix="% / year"
            type="number"
            step={0.1}
            min={0}
            max={10}
          />
          <InputField
            label="Current Years of Service"
            value={currentYearsOfService}
            onChange={setCurrentYearsOfService}
            suffix="years"
            type="number"
            min={0}
            max={45}
          />
          {!plan.useAgeFactor && (
            <InputField
              label="Benefit Multiplier"
              value={customMultiplier}
              onChange={setCustomMultiplier}
              suffix="% per year"
              type="number"
              step={0.05}
              min={0.5}
              max={4}
            />
          )}
        </div>
      </GlassCard>

      {/* Plan Details */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">Plan Parameters</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <PlanDetail label="Employee Contrib" value={`${plan.employeeContrib}%`} />
          <PlanDetail label="Employer Contrib" value={`${plan.employerContrib}%`} />
          <PlanDetail label="FAS Period" value={`${plan.fasYears} years`} />
          <PlanDetail label="Vesting" value={`${plan.vestingYears} years`} />
          <PlanDetail label="Unreduced Age" value={`${plan.unreducedAge}`} />
          <PlanDetail label="Unreduced Service" value={`${plan.unreducedYears} years`} />
        </div>
      </GlassCard>

      {/* Eligibility Status */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">Retirement Eligibility</h3>
        <div className="flex flex-wrap gap-2">
          <Badge variant={result.isVested ? 'success' : 'danger'}>
            {result.isVested ? 'Vested' : `Need ${plan.vestingYears - result.totalYearsOfService}yr to vest`}
          </Badge>
          <Badge variant={result.isUnreduced ? 'success' : result.earlyEligible ? 'warning' : 'danger'}>
            {result.isUnreduced ? 'Unreduced Benefit' : result.earlyEligible ? 'Early Retirement Eligible' : 'Not Yet Eligible'}
          </Badge>
          {planPreset === 'strs_ohio' && result.totalYearsOfService >= 35 && (
            <Badge variant="success">35yr Rule (Any Age)</Badge>
          )}
        </div>
        {!result.isUnreduced && result.totalYearsOfService < plan.unreducedYears && (
          <p className="text-xs text-muted mt-2">
            Need {plan.unreducedYears - result.totalYearsOfService} more years of service
            or age {plan.unreducedAge} for unreduced benefits
          </p>
        )}
      </GlassCard>

      {/* Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ResultCard label="Monthly Pension" value={formatCurrencyExact(result.monthlyBenefit)} color={colors.primary} highlight />
        <ResultCard label="Annual Pension" value={formatCurrency(result.annualBenefit)} color={colors.positive} />
        <ResultCard label="Replacement Ratio" value={`${result.replacementRatio}%`} color={result.replacementRatio >= 70 ? colors.positive : colors.warning} />
        <ResultCard label="Benefit Multiplier" value={`${result.multiplier}%`} color={colors.secondary} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ResultCard label="Final Average Salary" value={formatCurrency(result.fas)} color={colors.chartTick} />
        <ResultCard label="Years of Service" value={`${result.totalYearsOfService}`} color={colors.secondary} />
        <ResultCard label="Your Contributions" value={formatCurrency(result.totalEmployeeContrib)} color={colors.negative} />
        <ResultCard label="Employer Contributions" value={formatCurrency(result.totalEmployerContrib)} color={colors.positive} />
      </div>

      {/* Benefit Breakdown */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-3">Benefit Calculation</h3>
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="px-3 py-2 rounded-xl bg-subtle">
            <span className="text-muted">Multiplier </span>
            <span className="font-bold text-heading">{result.multiplier}%</span>
          </span>
          <span className="text-muted">&times;</span>
          <span className="px-3 py-2 rounded-xl bg-subtle">
            <span className="text-muted">Service </span>
            <span className="font-bold text-heading">{result.totalYearsOfService} yrs</span>
          </span>
          <span className="text-muted">&times;</span>
          <span className="px-3 py-2 rounded-xl bg-subtle">
            <span className="text-muted">FAS </span>
            <span className="font-bold text-heading">{formatCurrency(result.fas)}</span>
          </span>
          <span className="text-muted">=</span>
          <span className="px-3 py-2 rounded-xl" style={{ background: `${colors.primary}22` }}>
            <span className="font-bold" style={{ color: colors.primary }}>{formatCurrency(result.annualBenefit)}/yr</span>
          </span>
        </div>
      </GlassCard>

      {/* Age Projection Chart */}
      {result.projectionData.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Benefit by Retirement Age</h3>
          <div ref={chartRef} className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={result.projectionData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis
                  dataKey="age"
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `Age ${v}`}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    color: 'var(--heading)',
                  }}
                  formatter={(v, name) => [formatCurrency(v), name]}
                  labelFormatter={v => `Age ${v}`}
                />
                <Legend />
                <ReferenceLine x={retirementAge} stroke={colors.warning} strokeDasharray="5 5" label={{ value: 'Target', fill: colors.warning, fontSize: 11 }} />
                <Bar dataKey="annualBenefit" name="Annual Pension" fill={colors.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}

      {/* Replacement Ratio Chart */}
      {result.projectionData.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Income Replacement Ratio</h3>
          <p className="text-xs text-muted mb-4">Target: 70-80% of final salary to maintain lifestyle in retirement</p>
          <div ref={replacementChartRef} className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.projectionData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis
                  dataKey="age"
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `Age ${v}`}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `${v}%`}
                  domain={[0, 100]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    color: 'var(--heading)',
                  }}
                  formatter={(v) => [`${v}%`, 'Replacement Ratio']}
                  labelFormatter={v => `Age ${v}`}
                />
                <ReferenceLine y={70} stroke={colors.positive} strokeDasharray="5 5" label={{ value: '70% Target', fill: colors.positive, fontSize: 11 }} />
                <ReferenceLine x={retirementAge} stroke={colors.warning} strokeDasharray="5 5" />
                <Area type="monotone" dataKey="replacementRatio" name="Replacement %" stroke={colors.secondary} fill={colors.secondary} fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}

      {/* Projection Table */}
      <GlassCard className="!p-0 overflow-hidden">
        <div className="px-6 py-4 text-sm font-semibold text-body">
          Detailed Projection Table
        </div>
        <div className="overflow-x-auto border-t border-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-card">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted">Age</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Service</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">FAS</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Mult</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Annual</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Monthly</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Replace %</th>
              </tr>
            </thead>
            <tbody>
              {result.projectionData.map((r) => (
                <tr
                  key={r.age}
                  className={`border-b border-subtle hover:bg-subtle transition text-sm ${
                    r.age === retirementAge ? 'bg-subtle' : ''
                  }`}
                >
                  <td className="px-4 py-2 text-body">
                    {r.age}
                    {r.age === retirementAge && (
                      <span className="ml-1 text-xs text-primary font-medium">target</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-body">{r.yearsOfService}yr</td>
                  <td className="px-4 py-2 text-right text-heading">{formatCurrency(r.fas)}</td>
                  <td className="px-4 py-2 text-right text-body">{r.multiplier}%</td>
                  <td className="px-4 py-2 text-right text-heading font-medium">{formatCurrency(r.annualBenefit)}</td>
                  <td className="px-4 py-2 text-right text-positive font-medium">{formatCurrency(r.monthlyBenefit)}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={r.replacementRatio >= 70 ? 'text-positive' : 'text-warn'}>
                      {r.replacementRatio}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  )
}

function InputField({ label, value, onChange, prefix, suffix, type = 'text', step, min, max }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 ${
            prefix && suffix ? 'pl-7 pr-16' : prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-16' : 'px-3'
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>
        )}
      </div>
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

function PlanDetail({ label, value }) {
  return (
    <div className="p-2.5 rounded-xl bg-subtle text-center">
      <p className="text-xs text-muted">{label}</p>
      <p className="text-sm font-bold text-heading mt-0.5">{value}</p>
    </div>
  )
}
